import React, { createContext, useContext, useCallback, useRef, useState, useEffect } from "react";
import { CreateMLCEngine } from "@mlc-ai/web-llm";

const MODEL_ID = "Qwen2.5-3B-Instruct-q4f16_1-MLC";

function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);
}

const WebLLMContext = createContext({
  engine: null,
  ready: false,
  loading: false,
  downloading: false,
  progress: 0,
  error: null,
  isMobile: false,
  init: () => {},
  resetChat: () => {},
});

export const useWebLLM = () => useContext(WebLLMContext);

export default function WebLLMProvider({ children }) {
  const [engine, setEngine] = useState(null);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(null);
  const mobile = isMobileDevice();
  const started = useRef(false);

  const init = useCallback(() => {
    if (started.current || mobile) return;
    started.current = true;

    const load = async () => {
      setLoading(true);
      try {
        const eng = await CreateMLCEngine(MODEL_ID, {
          initProgressCallback: (report) => {
            setProgress(report.progress || 0);
            const text = (report.text || "").toLowerCase();
            if (text.includes("fetch") || text.includes("download")) {
              setDownloading(true);
            }
          },
        });
        setEngine(eng);
        setReady(true);
      } catch (err) {
        console.error("WebLLM load failed:", err);
        setError(err);
      } finally {
        setLoading(false);
        setDownloading(false);
      }
    };

    load();
  }, [mobile]);

  useEffect(() => {
    init();
  }, [init]);

  const resetChat = useCallback(async () => {
    if (engine) {
      try {
        await engine.resetChat();
      } catch (err) {
        console.error("Failed to reset chat:", err);
      }
    }
  }, [engine]);

  return (
    <WebLLMContext.Provider value={{ engine, ready, loading, downloading, progress, error, isMobile: mobile, init, resetChat }}>
      {children}
    </WebLLMContext.Provider>
  );
}
